"use client";

/**
 * Combobox — searchable dropdown for medium/large option lists.
 *
 * Use when an option list is long enough that scrolling a native <select> is
 * frustrating (≈ > 25 items). For small enums (≤ ~10 items) prefer the
 * native <Select> from @speakai/ui/input.
 *
 * Behavior modeled on Angular's `<sp-language-selector>` / `<sp-folder-select>`
 * (mat-autocomplete pattern). Keyboard parity:
 *   ArrowUp / ArrowDown — move highlighted option
 *   Enter              — commit highlighted option
 *   Esc                — close without commit
 *   Tab                — close + leave selection unchanged
 *   Click outside      — close without commit
 *
 * Filter defaults to case-insensitive substring match on `label`. Override via
 * the `filter` prop. Loading shows a spinner row inside the dropdown. Per-option
 * `disabled` renders the row greyed and unselectable. `topAction` pins a single
 * row above the list (e.g. "+ Create Folder").
 */

import {
  forwardRef,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, LoaderCircle, Search } from "lucide-react";
import { Tooltip } from "@speakai/ui/tooltip";
import { cn } from "@/lib/utils/cn";

export interface ComboboxOption {
  value: string;
  label: string;
  disabled?: boolean;
  description?: string;
  /**
   * Optional group label. When ANY option has a non-empty `group`, the
   * dropdown renders a small uppercase section header before each group.
   * Options without `group` go in an unlabeled first section.
   */
  group?: string;
  /** Hover tooltip for the option label — useful when the label is truncated. */
  tooltip?: string;
}

export interface ComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loading?: boolean;
  disabled?: boolean;
  /** `true` for red border only, or a string to display below the trigger. */
  error?: boolean | string;
  label?: string;
  className?: string;
  id?: string;
  /**
   * Trigger size. `default` matches the input height used elsewhere (h-10 / text-sm).
   * `sm` is for compact surfaces — table-cell rows, narrow toolbars (h-8 / text-xs).
   */
  size?: "default" | "sm";
  /** Optional pinned row at the top of the list (e.g. "+ Create Folder"). */
  topAction?: {
    label: ReactNode;
    onSelect: () => void;
  };
  /** Override the default substring-on-label filter. */
  filter?: (option: ComboboxOption, query: string) => boolean;
  /**
   * Called whenever the search text changes. Provide this to drive
   * server-side search: the consumer debounces the query, refetches, and
   * passes the results back as `options` (pair with `filter={() => true}` to
   * disable the built-in client-side filter).
   */
  onSearchChange?: (query: string) => void;
  /** Suppress the search input. Use for short enums (≤ ~10 options). */
  hideSearch?: boolean;
  "aria-label"?: string;
  "data-testid"?: string;
}

const PANEL_MIN_WIDTH = 260;
const VIEWPORT_GUTTER = 8;

const triggerBase =
  "flex w-full items-center justify-between rounded-lg border border-border bg-background text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";

const triggerSizes = {
  default: "h-10 px-3 py-2 text-sm",
  sm: "h-8 px-2 py-1 text-xs",
} as const;

export const Combobox = forwardRef<HTMLDivElement, ComboboxProps>(
  (
    {
      value,
      onChange,
      options,
      placeholder = "Select...",
      searchPlaceholder = "Search...",
      emptyMessage = "No matching results found",
      loading = false,
      disabled = false,
      error,
      label,
      className,
      id,
      size = "default",
      topAction,
      filter,
      onSearchChange,
      hideSearch = false,
      "aria-label": ariaLabel,
      "data-testid": testId,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    // Portal positioning: viewport-relative coords + width snapshotted from
    // the trigger. Recomputed on open, on scroll, and on resize so the panel
    // tracks the trigger even inside scroll containers.
    const [panelRect, setPanelRect] = useState<{
      top: number;
      left: number;
      width: number;
      placeAbove: boolean;
    } | null>(null);
    // Avoid SSR mismatch — only mount the portal after first render on the client.
    const [isMounted, setIsMounted] = useState(false);

    const generatedId = useId();
    const triggerId = id ?? `combobox-${generatedId}`;
    const listboxId = `${triggerId}-listbox`;

    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const optionRefs = useRef<Map<number, HTMLButtonElement | null>>(new Map());

    useEffect(() => {
      setIsMounted(true);
    }, []);

    // Notify consumers of search changes so they can drive server-side search.
    // Fires on typing and on every reset-to-"" path (close/commit/escape), so
    // a server-backed list refetches back to its unfiltered state on close.
    useEffect(() => {
      onSearchChange?.(search);
    }, [search, onSearchChange]);

    // Remember option data for every value that has appeared in `options`, so the
    // trigger keeps its label after a server-side search removes that option from
    // the current result set. Mirrors MultiCombobox's chip handling. Harmless for
    // client-side lists, where `options` always holds the full set.
    const seenOptionsRef = useRef(new Map<string, ComboboxOption>());
    useEffect(() => {
      options.forEach((o) => seenOptionsRef.current.set(o.value, o));
    }, [options]);

    const selectedOption = useMemo(
      () =>
        options.find((o) => o.value === value) ?? seenOptionsRef.current.get(value),
      [options, value],
    );

    const filteredOptions = useMemo(() => {
      if (!search.trim()) return options;
      const lower = search.trim().toLowerCase();
      const fn =
        filter ??
        ((o: ComboboxOption, q: string) => o.label.toLowerCase().includes(q));
      return options.filter((o) => fn(o, lower));
    }, [options, search, filter]);

    // Grouping is active when ANY option declares a non-empty `group`.
    // We preserve the original `options` order; ungrouped options form an
    // unlabeled first section. Filtered groups with zero matches are hidden.
    const isGrouped = useMemo(
      () => options.some((o) => o.group && o.group.trim().length > 0),
      [options],
    );
    const renderSections = useMemo(() => {
      if (!isGrouped) return null;
      const sections: { label: string | null; indices: number[] }[] = [];
      const byLabel = new Map<string | null, number>();
      filteredOptions.forEach((opt, idx) => {
        const key = opt.group && opt.group.trim().length > 0 ? opt.group : null;
        let sectionIdx = byLabel.get(key);
        if (sectionIdx === undefined) {
          sectionIdx = sections.length;
          byLabel.set(key, sectionIdx);
          sections.push({ label: key, indices: [] });
        }
        sections[sectionIdx].indices.push(idx);
      });
      return sections;
    }, [isGrouped, filteredOptions]);

    // Reset highlight when filter changes or dropdown opens.
    useEffect(() => {
      if (!isOpen) {
        setHighlightedIndex(-1);
        return;
      }
      // First non-disabled option, falling back to the currently selected
      // option's index when present so opening the dropdown lands on the
      // user's existing choice.
      const selectedIdx = filteredOptions.findIndex((o) => o.value === value);
      const firstEnabledIdx = filteredOptions.findIndex((o) => !o.disabled);
      setHighlightedIndex(
        selectedIdx >= 0 && !filteredOptions[selectedIdx]?.disabled
          ? selectedIdx
          : firstEnabledIdx,
      );
    }, [isOpen, filteredOptions, value]);

    // Close on outside click. The dropdown panel is portaled outside the
    // container, so we have to check both the in-flow trigger AND the
    // portaled panel before closing.
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        const insideTrigger =
          containerRef.current && containerRef.current.contains(target);
        const insidePanel = panelRef.current && panelRef.current.contains(target);
        if (!insideTrigger && !insidePanel) {
          setIsOpen(false);
          setSearch("");
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Compute panel position relative to the viewport. Flip above the trigger
    // when there isn't enough space below.
    const updatePosition = useCallback(() => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const panelEl = panelRef.current;
      const estimatedHeight = panelEl?.offsetHeight ?? 320;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const placeAbove =
        spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
      // The panel is at least PANEL_MIN_WIDTH wide; near the right edge it shifts left to stay on screen.
      const panelWidth = Math.max(rect.width, PANEL_MIN_WIDTH);
      const left = Math.max(VIEWPORT_GUTTER, Math.min(rect.left, window.innerWidth - panelWidth - VIEWPORT_GUTTER));
      setPanelRect({
        top: placeAbove ? rect.top : rect.bottom,
        left,
        width: rect.width,
        placeAbove,
      });
    }, []);

    // Recompute position on open + on scroll/resize while open. Capture-phase
    // scroll listener catches scroll events inside any ancestor (including
    // dialogs and tables with overflow).
    useLayoutEffect(() => {
      if (!isOpen) {
        setPanelRect(null);
        return;
      }
      updatePosition();
      const handler = () => updatePosition();
      window.addEventListener("scroll", handler, true);
      window.addEventListener("resize", handler);
      return () => {
        window.removeEventListener("scroll", handler, true);
        window.removeEventListener("resize", handler);
      };
    }, [isOpen, updatePosition]);

    // Close on Escape.
    useEffect(() => {
      if (!isOpen) return;
      const handleKey = (e: globalThis.KeyboardEvent) => {
        if (e.key === "Escape") {
          setIsOpen(false);
          setSearch("");
        }
      };
      document.addEventListener("keydown", handleKey);
      return () => document.removeEventListener("keydown", handleKey);
    }, [isOpen]);

    // Auto-focus search input on open. When hidden, focus first enabled option.
    useEffect(() => {
      if (!isOpen) return;
      requestAnimationFrame(() => {
        if (hideSearch) {
          const firstEnabledIdx = filteredOptions.findIndex((o) => !o.disabled);
          if (firstEnabledIdx >= 0) {
            optionRefs.current.get(firstEnabledIdx)?.focus();
          } else {
            panelRef.current?.focus();
          }
        } else {
          searchInputRef.current?.focus();
        }
      });
    }, [isOpen, hideSearch, filteredOptions]);

    // Scroll highlighted option into view. With hideSearch, also focus it
    // since keyboard nav lives on the option buttons.
    useEffect(() => {
      if (highlightedIndex < 0) return;
      const el = optionRefs.current.get(highlightedIndex);
      el?.scrollIntoView({ block: "nearest" });
      if (hideSearch && isOpen) {
        el?.focus();
      }
    }, [highlightedIndex, hideSearch, isOpen]);

    const moveHighlight = useCallback(
      (dir: 1 | -1) => {
        setHighlightedIndex((current) => {
          const len = filteredOptions.length;
          if (len === 0) return -1;
          let next = current < 0 ? (dir === 1 ? -1 : len) : current;
          for (let i = 0; i < len; i++) {
            next = (next + dir + len) % len;
            if (!filteredOptions[next].disabled) return next;
          }
          return current;
        });
      },
      [filteredOptions],
    );

    const commitSelection = useCallback(
      (idx: number) => {
        const opt = filteredOptions[idx];
        if (!opt || opt.disabled) return;
        onChange(opt.value);
        setIsOpen(false);
        setSearch("");
      },
      [filteredOptions, onChange],
    );

    const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        moveHighlight(1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        moveHighlight(-1);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlightedIndex >= 0) {
          commitSelection(highlightedIndex);
        } else if (topAction && filteredOptions.length === 0) {
          topAction.onSelect();
          setIsOpen(false);
          setSearch("");
        }
      } else if (e.key === "Tab") {
        setIsOpen(false);
        setSearch("");
      }
    };

    const toggle = () => {
      if (disabled || loading) return;
      setIsOpen((prev) => {
        if (!prev) setSearch("");
        return !prev;
      });
    };

    const errorMessage = typeof error === "string" && error ? error : null;
    const hasError = Boolean(error);

    const renderOption = (idx: number, opt: ComboboxOption) => {
      const isSelected = opt.value === value;
      const isHighlighted = idx === highlightedIndex;
      const labelNode = <span className="truncate">{opt.label}</span>;
      return (
        <button
          key={opt.value}
          ref={(el) => {
            optionRefs.current.set(idx, el);
          }}
          id={`${listboxId}-option-${idx}`}
          type="button"
          role="option"
          aria-selected={isSelected}
          aria-disabled={opt.disabled}
          disabled={opt.disabled}
          onMouseEnter={() => {
            if (!opt.disabled) setHighlightedIndex(idx);
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            if (!opt.disabled) commitSelection(idx);
          }}
          // onClick handles keyboard Enter/Space — mousedown does not fire for keyboard activation.
          onClick={() => {
            if (!opt.disabled) commitSelection(idx);
          }}
          onKeyDown={
            hideSearch
              ? (e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    moveHighlight(1);
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    moveHighlight(-1);
                  }
                }
              : undefined
          }
          className={cn(
            "flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors",
            opt.disabled
              ? "cursor-not-allowed text-muted-foreground opacity-50"
              : "cursor-pointer text-popover-foreground",
            isHighlighted && !opt.disabled && "bg-accent",
            isSelected && "font-medium",
          )}
        >
          <span className="flex min-w-0 flex-1 flex-col">
            {opt.tooltip ? (
              <Tooltip content={opt.tooltip}>{labelNode}</Tooltip>
            ) : (
              labelNode
            )}
            {opt.description && (
              <span className="truncate text-xs text-muted-foreground">
                {opt.description}
              </span>
            )}
          </span>
          {isSelected && (
            <Check
              aria-hidden="true"
              className="h-4 w-4 shrink-0 text-primary"
            />
          )}
        </button>
      );
    };

    return (
      <div
        ref={ref}
        className={cn("relative w-full", className)}
        data-testid={testId}
      >
        {label && (
          <label
            htmlFor={triggerId}
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            {label}
          </label>
        )}
        <div ref={containerRef} className="relative">
          <button
            ref={triggerRef}
            id={triggerId}
            type="button"
            disabled={disabled || loading}
            onClick={toggle}
            className={cn(
              triggerBase,
              triggerSizes[size],
              size === "sm" ? "pr-8 text-left" : "pr-10 text-left",
              !selectedOption && "text-muted-foreground",
              hasError && "border-danger focus-visible:ring-danger",
              (disabled || loading) && "cursor-not-allowed",
            )}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={listboxId}
            aria-label={ariaLabel}
            aria-invalid={hasError ? true : undefined}
          >
            <span className="truncate">
              {loading
                ? "Loading..."
                : selectedOption
                  ? selectedOption.label
                  : placeholder}
            </span>
          </button>
          <ChevronDown
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute top-1/2 -translate-y-1/2 text-muted-foreground transition-transform duration-150",
              size === "sm" ? "right-2 h-3 w-3" : "right-3 h-4 w-4",
              label && "top-[calc(50%+12px)]",
              isOpen && "rotate-180",
            )}
          />
          {isOpen &&
            isMounted &&
            panelRect &&
            createPortal(
              <div
                ref={panelRef}
                // Portaled to <body> so the panel escapes ancestor `overflow:
                // hidden` (tables, dialogs, sheets). `position: fixed` against
                // viewport coords keeps it anchored to the trigger.
                style={{
                  position: "fixed",
                  top: panelRect.placeAbove ? undefined : panelRect.top + 4,
                  bottom: panelRect.placeAbove
                    ? window.innerHeight - panelRect.top + 4
                    : undefined,
                  left: panelRect.left,
                  width: Math.max(panelRect.width, PANEL_MIN_WIDTH),
                }}
                className="z-[80] overflow-hidden rounded-lg border border-border bg-popover shadow-md"
              >
                {!hideSearch && (
                  <div className="border-b border-border p-2">
                    <div className="relative">
                      <Search
                        aria-hidden="true"
                        className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                      />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={searchPlaceholder}
                        className="w-full rounded-md border border-border bg-background pl-8 pr-2 py-1.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
                        aria-controls={listboxId}
                        aria-activedescendant={
                          highlightedIndex >= 0
                            ? `${listboxId}-option-${highlightedIndex}`
                            : undefined
                        }
                      />
                    </div>
                  </div>
                )}
                <div
                  id={listboxId}
                  role="listbox"
                  className="max-h-60 overflow-y-auto py-1"
                >
                  {topAction && (
                    <button
                      type="button"
                      className="flex w-full items-center border-b border-border px-3 py-2 text-left text-sm font-medium text-primary transition-colors hover:bg-accent"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        topAction.onSelect();
                        setIsOpen(false);
                        setSearch("");
                      }}
                    >
                      {topAction.label}
                    </button>
                  )}
                  {loading ? (
                    <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                      <LoaderCircle
                        aria-hidden="true"
                        className="h-4 w-4 animate-spin"
                      />
                      Loading...
                    </div>
                  ) : filteredOptions.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      {emptyMessage}
                    </div>
                  ) : isGrouped && renderSections ? (
                    renderSections.map((section, sectionIdx) => (
                      <div key={section.label ?? `__ungrouped__${sectionIdx}`}>
                        {section.label && (
                          <div
                            className={cn(
                              "mt-2 mb-1 px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                              sectionIdx > 0 && "border-t border-border pt-2",
                            )}
                          >
                            {section.label}
                          </div>
                        )}
                        {section.indices.map((idx) =>
                          renderOption(idx, filteredOptions[idx]),
                        )}
                      </div>
                    ))
                  ) : (
                    filteredOptions.map((opt, idx) => renderOption(idx, opt))
                  )}
                </div>
              </div>,
              document.body,
            )}
        </div>
        {errorMessage && (
          <p className="mt-1.5 text-xs text-danger">{errorMessage}</p>
        )}
      </div>
    );
  },
);

Combobox.displayName = "Combobox";
